import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, MoreVertical, Plus, RefreshCw, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmDialog from '../../components/MasterAdmin/ui/ConfirmDialog';
import {
  deleteMasterAdminAmbulanceBooking,
  listMasterAdminAmbulanceBookings,
  updateMasterAdminAmbulanceBookingPayment,
  type MasterAdminAmbulanceBookingRow,
} from '../../services/MasterAdmin/masterAdminAmbulanceBookingService';

// Use CommonJS require here to avoid transient TS module-resolution glitches in some environments.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const MasterAdminAmbulanceBookingModal = require('../../components/MasterAdmin/MasterAdminAmbulanceBookingModal').default;

function getShortCode(id: string): string {
  const parts = String(id || '').split('-').filter(Boolean);
  const lastPart = parts.length ? parts[parts.length - 1] : String(id || '');
  return lastPart.toUpperCase();
}

function formatAmbulanceType(type: string): string {
  return String(type || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatMoney(n: number): string {
  return `₹${Number(n || 0).toLocaleString('en-IN')}`;
}

function routeSummary(row: MasterAdminAmbulanceBookingRow): string {
  const stops = Array.isArray(row.routeStops) ? row.routeStops.filter((s) => s?.address) : [];
  if (stops.length >= 2) {
    return stops.map((s) => s.label || s.address).join(' → ');
  }
  if (row.pickupAddress || row.dropAddress) {
    return `${row.pickupAddress || '—'} → ${row.dropAddress || '—'}`;
  }
  return '—';
}

const MasterAdminAmbulanceBookingsPage: React.FC = () => {
  const [rows, setRows] = useState<MasterAdminAmbulanceBookingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editRow, setEditRow] = useState<MasterAdminAmbulanceBookingRow | null>(null);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [viewRow, setViewRow] = useState<MasterAdminAmbulanceBookingRow | null>(null);
  const [confirmPayRow, setConfirmPayRow] = useState<MasterAdminAmbulanceBookingRow | null>(null);
  const [deleteRow, setDeleteRow] = useState<MasterAdminAmbulanceBookingRow | null>(null);
  const [actionMenu, setActionMenu] = useState<{
    bookingId: string;
    top: number;
    right: number;
  } | null>(null);

  const fetchBookings = useCallback(async (search = '') => {
    try {
      setLoading(true);
      setError(null);
      const res = await listMasterAdminAmbulanceBookings({
        page: 1,
        limit: 100,
        search,
      });
      if (!res.success) throw new Error('Failed to load bookings');
      setRows(res.data.bookings || []);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Failed to load bookings');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchBookings();
  }, [fetchBookings]);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [
        row.bookingId,
        row.patientName,
        row.patientPhone,
        row.agencyDisplayName || '',
        row.customAgencyName || '',
        row.pickupAddress || '',
        row.dropAddress || '',
        row.ambulanceType || '',
        row.bookingStatus || '',
        row.paymentStatus || '',
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [rows, searchText]);

  const markRemainingPaid = async (row: MasterAdminAmbulanceBookingRow) => {
    if (!row.remainingAmount || row.remainingAmount <= 0) return;
    try {
      setPayingId(row.bookingId);
      const res = await updateMasterAdminAmbulanceBookingPayment(row.bookingId, {
        markRemainingPaid: true,
      });
      if (!res.success) {
        toast.error(res.message || 'Failed to update payment');
        return;
      }
      toast.success('Remaining marked as paid');
      await fetchBookings(searchText.trim());
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Failed to update payment');
      toast.error(msg);
    } finally {
      setPayingId(null);
    }
  };

  return (
    <div className="w-full min-w-0 space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-2xl text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          Outside-app ambulance bookings (call / walk-in). Separate from the app request queue.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="flex-1 sm:flex-initial inline-flex items-center justify-center rounded-md bg-blue-600 px-3.5 py-2 text-xs sm:text-sm font-medium text-white shadow-sm hover:bg-blue-700 transition-colors"
          >
            <Plus className="mr-1.5 h-4 w-4 shrink-0" />
            Add booking
          </button>
          <button
            type="button"
            onClick={() => void fetchBookings(searchText.trim())}
            disabled={loading}
            className="flex-1 sm:flex-initial inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3.5 py-2 text-xs sm:text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 transition-colors"
          >
            <RefreshCw className={`mr-1.5 h-4 w-4 shrink-0 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="rounded-lg bg-white shadow dark:bg-gray-800">
        <div className="border-b p-3 sm:p-4 dark:border-gray-700">
          <div className="relative w-full sm:max-w-md">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 text-xs sm:text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder="Search patient, phone, agency, booking…"
            />
          </div>
        </div>

        {/* Mobile View: Card List (< md breakpoint) */}
        <div className="block md:hidden divide-y divide-gray-200 dark:divide-gray-700">
          {loading ? (
            <div className="p-8 text-center text-sm text-gray-500">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
              Loading bookings…
            </div>
          ) : error ? (
            <div className="p-8 text-center text-sm text-red-600">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">
              {searchText
                ? 'No booking matches your search.'
                : 'No Master Admin ambulance bookings yet. Tap Add booking to create one.'}
            </div>
          ) : (
            filtered.map((row) => (
              <div key={row.bookingId} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="font-semibold text-gray-900 dark:text-white text-sm" title={row.bookingId}>
                      {getShortCode(row.bookingId)}
                    </span>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                      {new Date(row.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setViewRow(row)}
                      className="px-2 py-1 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-50"
                    >
                      View
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditRow(row);
                        setModalOpen(true);
                      }}
                      className="px-2 py-1 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-50"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setActionMenu((current) =>
                          current?.bookingId === row.bookingId
                            ? null
                            : {
                                bookingId: row.bookingId,
                                top: rect.bottom + 6,
                                right: Math.max(8, window.innerWidth - rect.right),
                              }
                        );
                      }}
                      className="rounded-md border border-gray-300 p-1 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                      aria-label="More actions"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs bg-gray-50 dark:bg-gray-700/30 p-2.5 rounded-md">
                  <div>
                    <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 block">Patient</span>
                    <span className="font-medium text-gray-900 dark:text-white truncate block">{row.patientName || '—'}</span>
                    <span className="text-gray-500 dark:text-gray-400 text-[11px] block">{row.patientPhone || '—'}{row.patientGender ? ` · ${row.patientGender}` : ''}</span>
                  </div>
                  <div>
                    <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 block">Agency</span>
                    <span className="font-medium text-gray-900 dark:text-white truncate block">{row.agencyDisplayName || '—'}</span>
                    {!row.vendorId && row.customAgencyName ? (
                      <span className="text-gray-500 text-[10px] block">Custom</span>
                    ) : null}
                  </div>
                  <div>
                    <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 block">Type</span>
                    <span className="text-gray-800 dark:text-gray-200 block">{formatAmbulanceType(row.ambulanceType)}</span>
                    <span className="text-gray-500 text-[11px] block">{row.pickupTimeType === 'SCHEDULED' ? 'Scheduled' : 'Immediate'}</span>
                  </div>
                  <div>
                    <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 block">Payment</span>
                    <span className="font-semibold text-gray-900 dark:text-white block">{formatMoney(row.totalPayableAmount)}</span>
                    <span className="text-gray-500 text-[11px] block">{row.paymentStatus}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-1">
                  <span className="truncate max-w-[70%]" title={routeSummary(row)}>
                    <strong className="text-gray-700 dark:text-gray-300 font-medium">Route: </strong>
                    {routeSummary(row)}
                  </span>
                  {row.paymentCollectionType === 'ADVANCE' && row.remainingAmount > 0 ? (
                    <button
                      type="button"
                      disabled={payingId === row.bookingId}
                      onClick={() => setConfirmPayRow(row)}
                      className="px-2 py-0.5 text-[11px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 dark:text-blue-300 dark:bg-blue-900/30 rounded"
                    >
                      {payingId === row.bookingId ? '…' : `Pay rem (${formatMoney(row.remainingAmount)})`}
                    </button>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop View: Table (>= md breakpoint) */}
        <div className="hidden md:block overflow-x-auto overflow-y-visible">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Booking
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Patient
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Agency
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Payment
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                    Loading bookings…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-red-600">
                    {error}
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">
                    {searchText
                      ? 'No booking matches your search.'
                      : 'No Master Admin ambulance bookings yet. Click Add booking to create one.'}
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.bookingId} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-4 py-4 text-sm">
                      <div className="font-medium text-gray-900 dark:text-white" title={row.bookingId}>
                        {getShortCode(row.bookingId)}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500">
                        {new Date(row.createdAt).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {row.patientName || '—'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {row.patientPhone || '—'}
                        {row.patientGender ? ` · ${row.patientGender}` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                      {row.agencyDisplayName || '—'}
                      {!row.vendorId && row.customAgencyName ? (
                        <div className="text-xs text-gray-500">Custom</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">
                      <div>{formatAmbulanceType(row.ambulanceType)}</div>
                      <div className="text-xs text-gray-500">
                        {row.pickupTimeType === 'SCHEDULED' ? 'Scheduled' : 'Immediate'}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {formatMoney(row.totalPayableAmount)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {row.paymentStatus}
                        {row.paymentCollectionType === 'ADVANCE'
                          ? ` · adv ${formatMoney(row.advancePaidAmount)}`
                          : ''}
                        {row.remainingAmount > 0
                          ? ` · rem ${formatMoney(row.remainingAmount)}`
                          : ''}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <div className="inline-block text-left">
                        <button
                          type="button"
                          onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setActionMenu((current) =>
                              current?.bookingId === row.bookingId
                                ? null
                                : {
                                    bookingId: row.bookingId,
                                    top: rect.bottom + 6,
                                    right: window.innerWidth - rect.right,
                                  }
                            );
                          }}
                          className="rounded-md border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                          aria-label="Open actions"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <MasterAdminAmbulanceBookingModal
        open={modalOpen}
        editBooking={editRow}
        onClose={() => {
          setModalOpen(false);
          setEditRow(null);
        }}
        onSaved={() => void fetchBookings(searchText.trim())}
      />

      {actionMenu ? (
        <>
          <button
            type="button"
            aria-label="Close action menu"
            className="fixed inset-0 z-[95] cursor-default bg-transparent"
            onClick={() => setActionMenu(null)}
          />
          <div
            className="fixed z-[100] w-36 rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
            style={{ top: actionMenu.top, right: actionMenu.right }}
          >
            {(() => {
              const row = rows.find((r) => r.bookingId === actionMenu.bookingId);
              if (!row) return null;
              return (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setViewRow(row);
                      setActionMenu(null);
                    }}
                    className="block w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    View
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditRow(row);
                      setModalOpen(true);
                      setActionMenu(null);
                    }}
                    className="block w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    Edit
                  </button>
                  {row.paymentCollectionType === 'ADVANCE' && row.remainingAmount > 0 ? (
                    <button
                      type="button"
                      disabled={payingId === row.bookingId}
                      onClick={() => {
                        setConfirmPayRow(row);
                        setActionMenu(null);
                      }}
                      className="block w-full px-3 py-2 text-left text-xs text-blue-700 hover:bg-blue-50 disabled:opacity-50 dark:text-blue-300 dark:hover:bg-blue-900/20"
                    >
                      {payingId === row.bookingId ? 'Updating…' : 'Mark paid'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setDeleteRow(row);
                      setActionMenu(null);
                    }}
                    className="block w-full px-3 py-2 text-left text-xs text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/20"
                  >
                    Delete
                  </button>
                </>
              );
            })()}
          </div>
        </>
      ) : null}

      {viewRow ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white p-5 shadow-xl dark:bg-gray-800">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Booking {getShortCode(viewRow.bookingId)}
              </h3>
              <button
                type="button"
                onClick={() => setViewRow(null)}
                className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
              >
                Close
              </button>
            </div>
            <div className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <p className="text-xs text-gray-500">Patient</p>
                <p className="text-gray-900 dark:text-white">
                  {viewRow.patientName} · {viewRow.patientPhone}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Agency</p>
                <p className="text-gray-900 dark:text-white">{viewRow.agencyDisplayName || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Type / Timing</p>
                <p className="text-gray-900 dark:text-white">
                  {formatAmbulanceType(viewRow.ambulanceType)} ·{' '}
                  {viewRow.pickupTimeType === 'SCHEDULED' ? 'Scheduled' : 'Immediate'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Payment</p>
                <p className="text-gray-900 dark:text-white">
                  {formatMoney(viewRow.totalPayableAmount)} · {viewRow.paymentStatus}
                </p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-xs text-gray-500">Route</p>
                <p className="text-gray-900 dark:text-white">{routeSummary(viewRow)}</p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(confirmPayRow)}
        title="Mark remaining amount as paid?"
        message={
          confirmPayRow
            ? `This will mark remaining ${formatMoney(confirmPayRow.remainingAmount)} as paid for booking ${getShortCode(confirmPayRow.bookingId)}.`
            : ''
        }
        tone="warning"
        confirmLabel="Mark paid"
        busy={Boolean(confirmPayRow && payingId === confirmPayRow.bookingId)}
        onCancel={() => setConfirmPayRow(null)}
        onConfirm={() => {
          if (!confirmPayRow) return;
          void markRemainingPaid(confirmPayRow).then(() => setConfirmPayRow(null));
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteRow)}
        title="Delete booking?"
        message={
          deleteRow
            ? `This will permanently delete booking ${getShortCode(deleteRow.bookingId)}.`
            : ''
        }
        tone="danger"
        confirmLabel="Delete"
        onCancel={() => setDeleteRow(null)}
        onConfirm={() => {
          if (!deleteRow) return;
          void (async () => {
            try {
              const res = await deleteMasterAdminAmbulanceBooking(deleteRow.bookingId);
              if (!res.success) {
                toast.error(res.message || 'Failed to delete booking');
                return;
              }
              toast.success('Booking deleted');
              setDeleteRow(null);
              await fetchBookings(searchText.trim());
            } catch (err: unknown) {
              const msg =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                (err instanceof Error ? err.message : 'Failed to delete booking');
              toast.error(msg);
            }
          })();
        }}
      />
    </div>
  );
};

export default MasterAdminAmbulanceBookingsPage;
